/**
 * Per-column filter predicates for the admin table filter mechanism. Pure,
 * standalone, no React — `useColumnFilters.ts` wires these into a table's
 * state, and each page only picks which builder fits each column (text
 * contains, exact-match select, or a min/max range) instead of writing its
 * own predicate. See docs/decisions/2026-07-31-admin-table-sort-filter.md.
 */

import { parseAmountToCents } from '@equinox/shared';

export interface FilterColumn<T, V> {
  /** Value the column starts at — an inactive filter. */
  initial: V;
  /** Whether the current value actually constrains anything, for the "N filters active" indicator. */
  isActive(value: V): boolean;
  matches(row: T, value: V): boolean;
}

/** Case-insensitive substring match — the "contains" filter for names, SKUs, and codes. */
export function textContainsColumn<T>(getText: (row: T) => string): FilterColumn<T, string> {
  return {
    initial: '',
    isActive: (value) => value.trim() !== '',
    matches: (row, value) => {
      const needle = value.trim().toLowerCase();
      if (needle === '') return true;
      return getText(row).toLowerCase().includes(needle);
    },
  };
}

/** Exact match against one of a fixed set of values — role, active/inactive, a category. `''` means "all", never a value a real row can have. */
export function selectColumn<T>(getValue: (row: T) => string): FilterColumn<T, string> {
  return {
    initial: '',
    isActive: (value) => value !== '',
    matches: (row, value) => value === '' || getValue(row) === value,
  };
}

export interface RangeFilterValue {
  min: string;
  max: string;
}

const EMPTY_RANGE: RangeFilterValue = { min: '', max: '' };

function parseFloatOrNull(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * Plain numeric range (quantities, percentages). A blank bound is
 * open-ended; a bound that fails to parse is ignored rather than treated as
 * "reject every row" — a half-typed filter should never look like "no
 * results" while the admin is still typing the second character.
 */
export function numberRangeColumn<T>(
  getValue: (row: T) => number | null | undefined,
): FilterColumn<T, RangeFilterValue> {
  return {
    initial: EMPTY_RANGE,
    isActive: (value) => value.min.trim() !== '' || value.max.trim() !== '',
    matches: (row, value) => {
      const min = parseFloatOrNull(value.min);
      const max = parseFloatOrNull(value.max);
      if (min == null && max == null) return true;
      const actual = getValue(row);
      if (actual == null) return false;
      if (min != null && actual < min) return false;
      if (max != null && actual > max) return false;
      return true;
    },
  };
}

/**
 * Money range in pesos-with-decimals, e.g. "125.50". Every bound goes
 * through `@equinox/shared`'s `parseAmountToCents` — the exact same parser
 * every money input in this app already uses (`MoneyInput`, `PriceInput`) —
 * so a filter and a price field never disagree about what "125.50" means or
 * what to do with "1,250" or a stray "$". An unparsable bound is ignored,
 * same reasoning as `numberRangeColumn`.
 */
export function moneyRangeColumn<T>(
  getValueCents: (row: T) => number | null | undefined,
): FilterColumn<T, RangeFilterValue> {
  return {
    initial: EMPTY_RANGE,
    isActive: (value) => value.min.trim() !== '' || value.max.trim() !== '',
    matches: (row, value) => {
      const min = value.min.trim() === '' ? null : parseAmountToCents(value.min);
      const max = value.max.trim() === '' ? null : parseAmountToCents(value.max);
      if (min == null && max == null) return true;
      const actual = getValueCents(row);
      if (actual == null) return false;
      if (min != null && actual < min) return false;
      if (max != null && actual > max) return false;
      return true;
    },
  };
}
